/*
 *| The service is used for user sign-in and sign-out. It's responsible for verifying user identities during sign-in and securely logging them out when they choose to sign out. This is essential for user authentication and security.
 */

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import jwt_decode from 'jwt-decode';
import * as moment from 'moment';
import { BehaviorSubject, Observable } from 'rxjs';
import { BACKBONE_URL } from '../../shared/constants';
import { SignedInUser, SignInRequest, SpecialRoles } from '../../shared/models/signin.model';



@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private userSubject: BehaviorSubject<SignedInUser>;
  public user: Observable<SignedInUser>;

  constructor(private httpClient: HttpClient) {
    if (this.isLoggedIn()) {
      this.userSubject = new BehaviorSubject<SignedInUser>(this.getSignedInUser());
      this.user = this.userSubject.asObservable();
    }
    else {
      this.userSubject = new BehaviorSubject<SignedInUser>(new SignedInUser());
      this.user = this.userSubject.asObservable();
    }
  }

  public get userValue(): SignedInUser {
    return this.userSubject.value;
  }

  public getUserDisplayName(): string {
    if (!this.isLoggedIn())
      return;

    let user = this.getSignedInUser();
    return user.FirstName;
  }

  //SIGN IN METHOD
  public signIn(request: SignInRequest): Observable<SignedInUser> {
    let address = BACKBONE_URL + 'Tokens/sign-in';
    console.log('API ADDRESS:', BACKBONE_URL);

    return this.httpClient.post<SignedInUser>(address, request);
  }

  //RETURN USER'S DETAILS
  private getSignedInUser(): SignedInUser {
    if (!this.isLoggedIn())
      return;

    let user = new SignedInUser();

    user.UserId = parseInt(localStorage.getItem("user-id"));
    user.FullName = localStorage.getItem("name");
    user.FirstName = localStorage.getItem("first-name");
    user.LastName = localStorage.getItem("last-name");
    user.Email = localStorage.getItem("email");
    user.AuthenticationToken = localStorage.getItem("token");
    user.TokenExpiresAt = this.getExpiration().toDate();
    user.IsAdmin = JSON.parse(localStorage.getItem("is-admin"));
    user.Image = localStorage.getItem("image");
    user.Roles = JSON.parse(localStorage.getItem("roles"));

    return user;
  }

  //IF USER IS LOGGED IN
  public isLoggedIn(): boolean {
    return moment().isBefore(this.getExpiration());
  }

  //EXPIRY DATE TIME
  private getExpiration() {
    const expiration = localStorage.getItem("expires-at");

    if (!expiration)
      return moment().subtract(2, 'minutes'); //RETURN PAST TIME SO SYSTEM CAN ASSUME USER IS NOT LOGGED IN

    const expiresAt = JSON.parse(expiration);
    return moment(expiresAt);
  }

  public setSessionPostTwoFactorAuth(authResult: SignedInUser, email: string) {
    //STORE DATA IN THE SESSION
    this.setSession(authResult, email);
    let user = this.getSignedInUser();
    this.userSubject.next(user);
  }

  //SET USER'S DETAILS IN A SESSION
  private setSession(authResult: SignedInUser, email: string) {

    //DECODE TOKEN
    var decoded = jwt_decode(authResult.AuthenticationToken);

    //USER'S ID
    localStorage.setItem('user-id', authResult.UserId.toString());

    //USER'S NAME
    localStorage.setItem('name', `${authResult.FirstName} ${authResult.LastName}`);
    localStorage.setItem('first-name', authResult.FirstName);
    localStorage.setItem('last-name', authResult.LastName);

    const expiresAt = moment(authResult.TokenExpiresAt);
    localStorage.setItem('email', email);
    localStorage.setItem('token', authResult.AuthenticationToken);
    localStorage.setItem('expires-at', JSON.stringify(expiresAt.valueOf()));
    localStorage.setItem('is-admin', authResult.IsAdmin.toString());
    localStorage.setItem('image', authResult.Image);

    localStorage.setItem('roles', JSON.stringify(authResult.Roles));

  }

  //TO DO: CALL METHODS ON THE BACKBONE WHICH REVOKE TOKENS
  public logout() {
    localStorage.removeItem("user-id");
    localStorage.removeItem("name");
    localStorage.removeItem("first-name");
    localStorage.removeItem("last-name");
    localStorage.removeItem("email");
    localStorage.removeItem("token");
    localStorage.removeItem("expires-at");
    localStorage.removeItem("is-admin");
    localStorage.removeItem("image");

    //REMOVE AZURE TOKEN AS WELL
    localStorage.removeItem('azure-token-for-pc');

    this.userSubject.next(null);
  }

  public getSpecialRoles(): SpecialRoles {
    let retRoles: SpecialRoles = new SpecialRoles();

    if (this.isLoggedIn()) {
      let user = this.getSignedInUser();
      if (user && user.Roles) {
        if (user.Roles.find(x => x == 'Super Admin')) {
          retRoles.isSuperAdmin = true;
        }
        if (user.Roles.find(x => x == 'Admin' || x == 'Super Admin')) { //IF A USER IS ONLY A SUPER ADMIN, HE IS STILL AN ADMIN
          retRoles.isAdmin = true;
        }
      }
    }

    return retRoles;
  }

}
